import React, { useState, useEffect } from 'react';
import { BrandingProvider } from '../../contexts/BrandingContext';

interface UniversalWaitingRoomProps {
  requestId: string;
  sessionId?: string;
}

interface SessionStatus {
  session_id: string;
  status: 'waiting' | 'accepted' | 'in_progress' | 'completed' | 'cancelled' | 'rejected';
  template_type: string;
  request_id: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
  participant_data: {
    customer_name?: string;
    customer_email?: string;
    agent_name?: string;
  };
  host_data: {
    company_name?: string;
  };
  metadata?: any;
  agent_id?: string;
  room_id?: string;
  videoUrl?: string;
  estimatedWaitTime?: string;
}

export const UniversalWaitingRoom: React.FC<UniversalWaitingRoomProps> = ({
  requestId,
  sessionId
}) => {
  const [sessionStatus, setSessionStatus] = useState<SessionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Parse sessionId from URL if not provided via props
  const urlParams = new URLSearchParams(window.location.search);
  const actualSessionId = sessionId || urlParams.get('sessionId') || '';

  // Fetch session status
  const fetchSessionStatus = async () => {
    try {
      // Use the sessionId (ext_sess_*) for the API call, not the requestId
      const response = await fetch(`https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/universal-status/${actualSessionId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSessionStatus(data.session);
        
        // If session is accepted, redirect to video session
        if (data.session.status === 'accepted' && data.session.videoUrl) {
          window.location.href = data.session.videoUrl;
          return;
        }
        
        // If session is rejected, redirect to customer-friendly review page
        if (data.session.status === 'rejected') {
          // Get the current org ID from the URL
          const currentPath = window.location.pathname;
          const orgMatch = currentPath.match(/^\/([a-zA-Z0-9_]+)/);
          const orgPrefix = orgMatch && orgMatch[1].startsWith('org_') ? `/${orgMatch[1]}` : '';
          
          // Redirect to request under review page with customer info
          const reviewUrl = `${orgPrefix}/templates/universal/request-under-review?customerName=${encodeURIComponent(data.session.participant_data?.customer_name || 'Customer')}&companyName=${encodeURIComponent(data.session.host_data?.company_name || 'Support Team')}&sessionId=${encodeURIComponent(data.session.session_id)}&category=${encodeURIComponent(data.session.metadata?.category || 'consultation')}`;
          
          console.log('[UniversalWaitingRoom] Request rejected, redirecting to review page:', reviewUrl);
          window.location.href = reviewUrl;
          return;
        }
      } else {
        throw new Error(data.error || 'Failed to fetch session status');
      }
    } catch (err) {
      console.error('Error fetching session status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Start polling for status updates
  useEffect(() => {
    // Only start fetching if we have a valid sessionId
    if (actualSessionId) {
      fetchSessionStatus();
      
      // Poll every 3 seconds for status updates
      const interval = setInterval(fetchSessionStatus, 3000);
      setPollingInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      setError('Session ID is missing. Please check your URL.');
      setLoading(false);
    }
  }, [actualSessionId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Session...</h2>
          <p className="text-gray-600">Please wait while we prepare your session.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!sessionStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Not Found</h2>
          <p className="text-gray-600">The requested session could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <BrandingProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        {/* Custom CSS for Glow Animation */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes glow-sweep {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(100%);
              }
            }
            
            .animate-glow-sweep {
              animation: glow-sweep 2s ease-in-out infinite;
            }
            
            /* Additional glow effect */
            .glow-box {
              box-shadow: 
                0 0 20px rgba(59, 130, 246, 0.15),
                0 0 40px rgba(59, 130, 246, 0.1),
                inset 0 0 20px rgba(59, 130, 246, 0.05);
            }
          `
        }} />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome, {sessionStatus.participant_data?.customer_name || 'Customer'}!
              </h1>
              <p className="text-gray-600">
                You're in the waiting room for your universal session
              </p>
            </div>

            {/* Status Card with Animated Glow */}
            <div className="relative bg-white rounded-lg shadow-lg p-6 mb-6 overflow-hidden glow-box">
              {/* Animated Glow Effect */}
              <div className="absolute inset-0 opacity-40">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-300 to-transparent w-full h-full animate-glow-sweep"></div>
              </div>
              
              {/* Secondary glow layer for more intensity */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-200 to-transparent w-[150%] h-full animate-glow-sweep" style={{animationDelay: '0.5s'}}></div>
              </div>
              
              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900">Session Status</h2>
                  <div className="flex items-center">
                    <div className="animate-pulse w-3 h-3 bg-blue-600 rounded-full mr-2 shadow-lg shadow-blue-400 ring-2 ring-blue-200"></div>
                    <span className="text-sm font-medium text-blue-600 capitalize font-semibold">
                      {sessionStatus.status === 'waiting' ? 'Waiting for host' : sessionStatus.status}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Session ID:</span>
                  <span className="font-medium">{sessionStatus.session_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Template Type:</span>
                  <span className="font-medium capitalize">{sessionStatus.template_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium capitalize ${
                    sessionStatus.status === 'accepted' ? 'text-green-600' :
                    sessionStatus.status === 'waiting' ? 'text-yellow-600' :
                    sessionStatus.status === 'rejected' ? 'text-blue-600' :
                    'text-gray-600'
                  }`}>
                    {sessionStatus.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Company:</span>
                  <span className="font-medium">{sessionStatus.host_data?.company_name || 'Universal Support'}</span>
                </div>
                {sessionStatus.estimatedWaitTime && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Wait:</span>
                    <span className="font-medium">{sessionStatus.estimatedWaitTime}</span>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Waiting Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <div className="text-blue-600 mb-3">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Please wait for your host to join
              </h3>
              <p className="text-blue-700 mb-4">
                {sessionStatus.participant_data?.agent_name || 'Your agent'} from {sessionStatus.host_data?.company_name || 'Universal Support'} will be with you shortly.
                You'll automatically be connected when they're ready.
              </p>
              <p className="text-sm text-blue-600">
                This page will automatically update - no need to refresh.
              </p>
            </div>

            {/* Request Details */}
            {sessionStatus.metadata?.description && (
              <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Session Details</h3>
                <p className="text-gray-700">{sessionStatus.metadata.description}</p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-8 text-sm text-gray-500">
              <p>Request ID: {requestId}</p>
              <p>Session ID: {actualSessionId}</p>
              <p className="mt-1">Need help? Contact support at help@customerconnects.app</p>
            </div>
          </div>
        </div>
      </div>
    </BrandingProvider>
  );
}; 